/**
 * Building blocks shared by every Google Ads objective workspace
 * (src/pages/google-ads/<objective>/). Each objective owns its own pages; the
 * pieces here are the parts that are identical across them — money inputs,
 * chip toggles and image uploads → Google assets. Wizard and list-page
 * chrome live in wizardChrome.tsx and workspaceChrome.tsx.
 *
 * All components are MODULE SCOPE on purpose: defining them inside a page
 * remounts them on each keystroke and loses focus.
 */
import { useRef, useState } from 'react';
import { Input } from '@/components/Input';
import { LibraryFile, uploadsService } from '@/services/uploads.service';
import { MediaLibraryModal } from '@/components/MediaLibraryModal';
import { GoogleAdsAssetRef } from '@/services/google-ads.service';
import { Check, CheckCircle2, Circle, ImagePlus, Images, X, XCircle } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { errMsg, Spinner } from '@/components/google-ads/youtube/youtubeShared';
import { CurrencyTip } from '@/components/ads/BillingCurrency';
import { imagekitTransformUrl } from '@/components/google-ads/app-promotion/appPromotionShared';

export const uid = () => Math.random().toString(36).slice(2, 10);
export const toMicros = (v: string) => Math.round((Number(v) || 0) * 1_000_000);
export const fromMicros = (m?: string | number | null) => (m == null ? '' : String(Number(m) / 1_000_000));
export const today = () => new Date().toISOString().slice(0, 10);
export const isHttpUrl = (u: string) => /^https?:\/\/\S+$/i.test(u.trim());
export const hostOf = (u: string) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return 'example.com'; } };

export type Pub = 'idle' | 'running' | 'done' | 'failed';

export const StepIcon = ({ s }: { s?: Pub }) =>
  s === 'done' ? <CheckCircle2 size={15} className="shrink-0 text-success" />
  : s === 'running' ? <Spinner size={15} className="text-primary" />
  : s === 'failed' ? <XCircle size={15} className="shrink-0 text-danger" />
  : <Circle size={15} className="shrink-0 text-text-muted/40" />;

// ── small inputs ─────────────────────────────────────────────────────────

export function MoneyInput({ value, onChange, currency, placeholder, disabled }: {
  value: string; onChange: (v: string) => void; currency?: string | null; placeholder?: string; disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <Input value={value} onChange={(e) => onChange(e.target.value)} inputMode="decimal" placeholder={placeholder ?? '0.00'} disabled={disabled} />
      <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-text-muted">
        {currency ?? '—'} <CurrencyTip platform="google" currency={currency} />
      </span>
    </div>
  );
}

export const ChipToggle = ({ on, onClick, label, small }: { on: boolean; onClick: () => void; label: string; small?: boolean }) => (
  <button type="button" onClick={onClick}
    className={clsx(
      'inline-flex items-center gap-1 rounded-full border font-medium transition-colors',
      small ? 'px-2.5 py-1 text-2xs' : 'px-3 py-1.5 text-xs',
      on ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-surface text-text-secondary hover:border-primary/40 hover:text-text-primary',
    )}>
    {on && <Check size={small ? 10 : 12} />}{label}
  </button>
);

// ── image assets ─────────────────────────────────────────────────────────

export interface ImageDraft {
  id: string; url: string; note: string; width?: number; height?: number;
  /** The URL carries an ImageKit crop / pad, so Google receives exactly the ratio it asks for. */
  cropped?: boolean;
  /** Set once Google has the asset — publishing is resumable. */
  resourceName?: string;
}

export interface ImageSpec {
  label: string;
  /** "1.91:1" */
  short: string;
  ratio: number;
  /** ImageKit aspect token for `ar-…` (1.91:1 → 191-100). */
  ar: string;
  minW: number; minH: number;
  /** Recommended upload size. */
  rec: string;
  /** Photos are smart-cropped; logos are padded so no part of the mark is ever cut off. */
  fit: 'crop' | 'pad';
  hint: string;
}
export const IMAGE_SPECS = {
  landscape: { label: 'Landscape (1.91:1)', short: '1.91:1', ratio: 1.91, ar: '191-100', minW: 600, minH: 314, rec: '1200×628', fit: 'crop', hint: 'e.g. 1200×628' },
  square: { label: 'Square (1:1)', short: '1:1', ratio: 1, ar: '1-1', minW: 300, minH: 300, rec: '1200×1200', fit: 'crop', hint: 'e.g. 1200×1200' },
  portrait: { label: 'Portrait (4:5)', short: '4:5', ratio: 0.8, ar: '4-5', minW: 480, minH: 600, rec: '960×1200', fit: 'crop', hint: 'e.g. 960×1200' },
  logo: { label: 'Square logo (1:1)', short: '1:1', ratio: 1, ar: '1-1', minW: 128, minH: 128, rec: '1200×1200', fit: 'pad', hint: 'e.g. 1200×1200, transparent or white background' },
  logoWide: { label: 'Landscape logo (4:1)', short: '4:1', ratio: 4, ar: '4-1', minW: 512, minH: 128, rec: '1200×300', fit: 'pad', hint: 'e.g. 1200×300' },
} satisfies Record<string, ImageSpec>;

/** Google's own tolerance on these ratios is ~1%; anything further off gets cropped / padded. */
const RATIO_TOLERANCE = 0.01;
const MAX_EDGE = 1200;
/** Google image assets must be JPEG, PNG or GIF — other formats are converted on the fly. */
const NEEDS_FORMAT = /\.(webp|avif|heic|heif|bmp|tiff?)(\?|$)/i;

export interface FittedImage { url: string; width?: number; height?: number; cropped: boolean; problem?: string }

/**
 * Same method as App Promotion: images that don't match the required shape are
 * reshaped through an ImageKit URL transform (`/tr:…/` PATH form — the `?tr=`
 * query form is ignored on this account). No re-upload, no AI credits.
 */
export function fitImage(url: string, width: number | undefined, height: number | undefined, spec: ImageSpec): FittedImage {
  const format = NEEDS_FORMAT.test(url) ? ',f-png' : '';
  if (!url || !width || !height) return { url, width, height, cropped: false };
  const r = width / height;
  if (Math.abs(r - spec.ratio) / spec.ratio <= RATIO_TOLERANCE) {
    const problem = width < spec.minW || height < spec.minH ? `${width}×${height} is below Google's minimum ${spec.minW}×${spec.minH}` : undefined;
    const converted = format ? imagekitTransformUrl(url, format.slice(1)) : null;
    return { url: converted ?? url, width, height, cropped: false, problem };
  }
  // pad: the whole logo is kept, letter-boxed on white. crop: the largest smart-focused
  // crop of the original with the target shape. Both capped at 1200 wide.
  const w = spec.fit === 'pad'
    ? Math.min(r > spec.ratio ? width : Math.round(height * spec.ratio), MAX_EDGE)
    : Math.min(r > spec.ratio ? Math.round(height * spec.ratio) : width, MAX_EDGE);
  const h = Math.round(w / spec.ratio);
  const transform = spec.fit === 'pad'
    ? `w-${w},h-${h},cm-pad_resize,bg-FFFFFF${format}`
    : `ar-${spec.ar},w-${w},fo-auto${format}`;
  const fitted = imagekitTransformUrl(url, transform);
  if (!fitted) {
    return { url, width, height, cropped: false, problem: `${width}×${height} isn't ${spec.short} and this file can't be reshaped automatically — upload it instead` };
  }
  const problem = w < spec.minW || h < spec.minH
    ? `${spec.fit === 'pad' ? 'padded' : 'cropped'} to ${spec.short} it would be ${w}×${h}, below Google's minimum ${spec.minW}×${spec.minH}`
    : undefined;
  return { url: fitted, width: w, height: h, cropped: true, problem };
}

/**
 * Upload → ImageKit (slightor/<companyUid>/google-ads/<area>, scoped server-side)
 * → draft entry, reshaped to the spec before Google ever sees it. `onChange`
 * takes an UPDATER so several files dropped at once never clobber each other.
 */
export type ImageListUpdate = (update: (cur: ImageDraft[]) => ImageDraft[]) => void;

export function ImageGrid({ label, spec, images, onChange, min = 0, max, folder, disabled, optional }: {
  label: string; spec: ImageSpec; images: ImageDraft[]; onChange: ImageListUpdate;
  min?: number; max: number; folder: string; disabled?: boolean; optional?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const verb = spec.fit === 'pad' ? 'padded' : 'cropped';

  const fromLibrary = (files: LibraryFile[]) => onChange((cur) => {
    const room = max - cur.length;
    const fresh = files.filter((f) => !cur.some((x) => x.url === f.url)).slice(0, Math.max(0, room));
    if (fresh.length < files.length) toast.error(`Max ${max} images here.`);
    const made: ImageDraft[] = [];
    for (const f of fresh) {
      const fit = fitImage(f.url, f.width, f.height, spec);
      if (fit.problem) { toast.error(`${f.name}: ${fit.problem}`); continue; }
      made.push({ id: uid(), url: fit.url, note: f.name, width: fit.width, height: fit.height, cropped: fit.cropped });
    }
    return [...cur, ...made];
  });

  const upload = async (list: FileList | File[] | null) => {
    if (!list) return;
    const files = Array.from(list).filter((f) => /^image\//.test(f.type));
    const room = max - images.length;
    const picked = files.slice(0, Math.max(0, room));
    if (picked.length < files.length) toast.error(`Max ${max} images here.`);
    await Promise.all(picked.map(async (file) => {
      const id = uid();
      onChange((cur) => [...cur, { id, url: '', note: 'Uploading…' }]);
      try {
        const kit = await uploadsService.imagekit(file, folder);
        if (!kit.url) throw new Error('Upload returned no URL.');
        const fit = fitImage(kit.url, kit.width, kit.height, spec);
        if (fit.problem) throw new Error(fit.problem);
        if (fit.cropped) toast(`${file.name}: ${verb} to ${spec.short} (${fit.width}×${fit.height})`, { icon: '✂️' });
        onChange((cur) => cur.map((i) => (i.id === id ? { id, url: fit.url, note: file.name, width: fit.width, height: fit.height, cropped: fit.cropped } : i)));
      } catch (e) {
        onChange((cur) => cur.filter((i) => i.id !== id));
        toast.error(`${file.name}: ${errMsg(e)}`);
      }
    }));
  };

  const filled = images.filter((i) => i.url).length;
  const tileW = Math.round(76 * Math.max(0.8, Math.min(spec.ratio, 2.4)));
  const canAdd = images.length < max && !disabled;
  return (
    <div
      onDragOver={(e) => { if (!canAdd) return; e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { if (!canAdd) return; e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }}
      className={clsx('rounded-xl border p-3 transition-colors', dragging ? 'border-primary bg-primary-soft/30' : 'border-border bg-surface')}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold text-text-primary">{label}{optional && <span className="ml-1 font-normal text-text-muted">(optional)</span>}</span>
        <span className={clsx('shrink-0 text-2xs tabular-nums', filled < min ? 'text-warning' : 'text-text-muted')}>
          {filled}/{max}{filled < min ? ` · need ${min}` : ''}
        </span>
      </div>
      <p className="mt-0.5 text-2xs leading-4 text-text-muted">
        {spec.short} · min {spec.minW}×{spec.minH} · best {spec.rec}. Other shapes are {verb} automatically.
      </p>
      <div className="mt-2.5 flex flex-wrap gap-2">
        {images.map((im) => (
          <div key={im.id} className="group relative h-[76px] overflow-hidden rounded-lg border border-border bg-surface-alt" style={{ width: tileW }}
            title={`${im.note}${im.width && im.height ? ` · ${im.width}×${im.height}` : ''}`}>
            {im.url
              ? <img src={im.url} alt="" className={clsx('h-full w-full', spec.fit === 'pad' ? 'bg-white object-contain' : 'object-cover')} />
              : <div className="flex h-full items-center justify-center text-text-muted"><Spinner size={14} /></div>}
            {im.resourceName
              ? <span className="absolute bottom-1 left-1 rounded bg-success/90 px-1 text-[9px] font-semibold text-white">on Google</span>
              : im.cropped && <span className="absolute bottom-1 left-1 rounded bg-black/65 px-1 text-[9px] font-medium text-white">{verb}</span>}
            {!disabled && im.url && (
              <button type="button" onClick={() => onChange((cur) => cur.filter((x) => x.id !== im.id))} aria-label={`Remove ${im.note}`}
                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/65 text-white opacity-0 transition-opacity hover:bg-danger group-hover:opacity-100 focus-visible:opacity-100">
                <X size={11} />
              </button>
            )}
          </div>
        ))}
        {canAdd && (
          <>
            <button type="button" onClick={() => setLibraryOpen(true)} title="Pick from images this workspace already uploaded"
              className="flex h-[76px] w-[88px] flex-col items-center justify-center gap-1 rounded-lg border border-border bg-surface text-text-muted transition-colors hover:border-primary/50 hover:text-primary">
              <Images size={16} /><span className="text-[10px] font-medium">Library</span>
            </button>
            <button type="button" onClick={() => inputRef.current?.click()}
              className="flex h-[76px] w-[88px] flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border-strong text-text-muted transition-colors hover:border-primary/50 hover:text-primary">
              <ImagePlus size={16} /><span className="text-[10px] font-medium">Upload</span>
            </button>
          </>
        )}
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden"
          onChange={(e) => { upload(e.target.files); e.currentTarget.value = ''; }} />
      </div>
      <MediaLibraryModal open={libraryOpen} onClose={() => setLibraryOpen(false)} folder={folder} type="image"
        title={`${label} — your library`} validate={(f) => fitImage(f.url, f.width, f.height, spec).problem ?? null} onPick={fromLibrary} />
    </div>
  );
}

/** Resolve drafts → Google asset resource names, creating only what's missing and writing the ids back. */
export async function ensureImageAssets(
  images: ImageDraft[],
  create: (body: { imageUrl: string; name?: string }) => Promise<GoogleAdsAssetRef>,
  write: (id: string, resourceName: string) => void,
): Promise<string[]> {
  const out: string[] = [];
  for (const im of images) {
    if (!im.url) continue;
    if (im.resourceName) { out.push(im.resourceName); continue; }
    const a = await create({ imageUrl: im.url, name: im.note || undefined });
    write(im.id, a.resourceName);
    out.push(a.resourceName);
  }
  // Identical bytes dedupe to ONE Google asset; the same id twice in a field is refused (DUPLICATE_ASSET).
  return Array.from(new Set(out));
}

/** Shared list of image assets for the account, so the same upload isn't sent twice across pages in a session. */
export const assetCache = new Map<string, string>();
export const createAssetCached = (accountUid: string, fn: (b: { imageUrl: string; name?: string }) => Promise<GoogleAdsAssetRef>) =>
  async (b: { imageUrl: string; name?: string }) => {
    const k = `${accountUid}:${b.imageUrl}`;
    const hit = assetCache.get(k);
    if (hit) return { resourceName: hit, id: hit.split('/').pop() ?? '', type: 'IMAGE', reused: true } as GoogleAdsAssetRef;
    const a = await fn(b);
    assetCache.set(k, a.resourceName);
    return a;
  };
